import { useState, useEffect } from 'react';
import Loader from "../Shared/Loader/Loader";
import APIConstant from "../../utils/pathConstants";
import api from "../../utils/apiClient";
import CommonConfig from '../../utils/constant';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { useCatchResponse } from '../../utils/useCatchResponse';
import Toast from "../Shared/Toast/Toast";

const UserAccessManagement = () => {
    const { catchResponse } = useCatchResponse();
    const [roleList, setRoleList] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [userOptions, setUserOptions] = useState([]);
    const [selectedRole, setSelectedRole] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [serviceList, setServiceList] = useState([]);
    const [targetName, setTargetName] = useState('');

    useEffect(() => { init(); }, []);

    const init = async () => {
        try {
            Loader.show();
            const res = await api.post(APIConstant.path.GetManageableUsers, {});
            if (res.success) {
                setRoleList((res?.data?.manageableRoles || []).map(r => ({ label: r.RoleName, value: r._id })));
                setAllUsers(res?.data?.users || []);
            }
            Loader.hide();
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const handleRoleChange = (value) => {
        setSelectedRole(value);
        setSelectedUser(null);
        setServiceList([]);
        setTargetName('');
        setUserOptions(
            allUsers
                .filter(u => String(u.UserType) === String(value))
                .map(u => ({ label: `${u.Name}${u.RoleName ? ` (${u.RoleName})` : ''}`, value: u._id }))
        );
    };

    const handleUserChange = async (value) => {
        setSelectedUser(value);
        setServiceList([]);
        setTargetName('');
        if (!value) return;
        try {
            Loader.show();
            const res = await api.post(APIConstant.path.GetManagedUserAccess, { TargetUserId: value });
            if (res.success) {
                setServiceList(res?.data?.ServiceList || []);
                setTargetName(res?.data?.UserName || '');
            } else {
                Toast.error({ message: res?.message || 'Failed to load access' });
            }
            Loader.hide();
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const toggleChecked = (serviceId) => {
        setServiceList(list => list.map(s =>
            String(s.ServiceId) === String(serviceId) ? { ...s, IsChecked: !s.IsChecked } : s));
    };

    const toggleFlag = (serviceId, flag) => {
        setServiceList(list => list.map(s =>
            String(s.ServiceId) === String(serviceId)
                ? { ...s, AccessMasterList: { ...s.AccessMasterList, [flag]: !s.AccessMasterList?.[flag] } }
                : s));
    };

    const handleSave = async () => {
        if (!selectedUser) { Toast.error({ message: 'Please select a user' }); return; }
        try {
            Loader.show();
            const res = await api.post(APIConstant.path.SaveManagedUserAccess, {
                TargetUserId: selectedUser,
                ServiceList: serviceList,
            });
            Loader.hide();
            if (res.success) Toast.success({ message: res?.message || 'Access updated successfully' });
            else Toast.error({ message: res?.message || 'Failed to update access' });
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const FLAGS = [['AddAccess', 'Add'], ['EditAccess', 'Edit'], ['ViewAccess', 'View'], ['DeleteAccess', 'Delete']];
    const FLAG_COL = 62;
    const FLAGS_WIDTH = FLAG_COL * FLAGS.length;

    const setAllChecked = (value) => {
        setServiceList(list => list.map(s => value
            ? { ...s, IsChecked: true }
            : {
                ...s,
                IsChecked: false,
                AccessMasterList: {
                    ...s.AccessMasterList,
                    AddAccess: false,
                    EditAccess: false,
                    ViewAccess: false,
                    DeleteAccess: false,
                    OtherAccess: false,
                },
            }));
    };

    const flagCells = (item) => (
        <div className="p-d-flex p-ai-center" style={{ width: FLAGS_WIDTH, flex: '0 0 auto' }}>
            {FLAGS.map(([flag]) => (
                <div key={flag} style={{ width: FLAG_COL, textAlign: 'center' }}>
                    <Checkbox inputId={`${item.ServiceId}_${flag}`} checked={!!item.AccessMasterList?.[flag]}
                        onChange={() => toggleFlag(item.ServiceId, flag)} />
                </div>
            ))}
            {item.ServiceName === 'Co-Scholastic Grading' && (
                <span className="p-d-flex p-ai-center" style={{ gap: '6px', marginLeft: '10px' }}>
                    <Checkbox inputId={`${item.ServiceId}_OtherAccess`} checked={!!item.AccessMasterList?.OtherAccess}
                        onChange={() => toggleFlag(item.ServiceId, 'OtherAccess')} />
                    <label htmlFor={`${item.ServiceId}_OtherAccess`} style={{ cursor: 'pointer', fontSize: '12px' }}>Complete Edit</label>
                </span>
            )}
        </div>
    );

    const menuRow = (item, isChild) => (
        <div key={item.ServiceId} className="uam-row p-d-flex p-ai-center p-jc-between"
            style={{
                padding: '9px 14px', borderBottom: '1px solid #EEF2F8',
                paddingLeft: isChild ? '42px' : '14px',
                background: isChild ? '#fff' : '#F4F7FD',
            }}>
            <span className="p-d-flex p-ai-center" style={{ gap: '10px', flex: 1, minWidth: 0 }}>
                {isChild && <span style={{ color: '#C3CCDD', fontSize: '12px' }}>└</span>}
                <Checkbox inputId={`${item.ServiceId}_chk`} checked={!!item.IsChecked}
                    onChange={() => toggleChecked(item.ServiceId)} />
                <label htmlFor={`${item.ServiceId}_chk`}
                    style={{ cursor: 'pointer', fontWeight: isChild ? 400 : 600, color: '#1E2A44',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.ServiceName}
                </label>
            </span>
            {flagCells(item)}
        </div>
    );

    const parents = serviceList.filter(s => s.IsParent === 1);
    const grantedCount = serviceList.filter(s => s.IsChecked).length;

    return (
        <div className='wayment-owner-dashboard et-master-page'>
            <style>{`.uam-row:hover{background:#EDF2FC !important;}`}</style>

            <div className="heading-area p-d-flex p-ai-center p-jc-between">
                <div className="p-d-flex p-ai-baseline">
                    <i className="pi pi-lock"></i>
                    <h2>User Access Management</h2>
                </div>
            </div>

            <div className="p-d-flex p-ai-end p-flex-wrap p-mb-3"
                style={{ gap: '14px', padding: '14px 16px', margin: '0 8px', background: '#fff',
                    border: '1px solid #E4E9F2', borderRadius: '8px' }}>
                <div>
                    <p className="p-mb-1" style={{ fontSize: '13px', color: '#64748B' }}>Role</p>
                    <Dropdown value={selectedRole} options={roleList} onChange={(e) => handleRoleChange(e.value)}
                        placeholder="Select Role" style={{ minWidth: '180px', height: '38px' }} />
                </div>
                <div>
                    <p className="p-mb-1" style={{ fontSize: '13px', color: '#64748B' }}>User</p>
                    <Dropdown value={selectedUser} options={userOptions} onChange={(e) => handleUserChange(e.value)}
                        placeholder="Select User" filter showClear disabled={!selectedRole}
                        style={{ minWidth: '240px', height: '38px' }} emptyMessage="No users" />
                </div>
                <div style={{ flex: 1 }}></div>
                <div>
                    <Button label="Save Access" icon="pi pi-save" className="btn-text-action-primary"
                        disabled={!selectedUser} onClick={handleSave} />
                </div>
            </div>

            {roleList.length === 0 && (
                <div style={{ padding: '14px 8px 0', color: '#64748B' }}>You do not have permission to manage any users.</div>
            )}

            {selectedUser && (
                <div style={{ margin: '12px 8px 0', border: '1px solid #E4E9F2', borderRadius: '8px', overflow: 'hidden' }}>
                    <div className="p-d-flex p-ai-center p-jc-between"
                        style={{ padding: '10px 14px', background: '#F8F9FC', borderBottom: '1px solid #E4E9F2' }}>
                        <span style={{ fontWeight: 600, color: '#1E2A44' }}>
                            <i className="pi pi-user" style={{ marginRight: '8px', color: '#0A1F44' }}></i>
                            {targetName}
                            <span style={{ fontWeight: 400, color: '#64748B', marginLeft: '10px', fontSize: '13px' }}>
                                {grantedCount} menu{grantedCount === 1 ? '' : 's'} granted
                            </span>
                        </span>
                        {serviceList.length > 0 && (
                            <span className="p-d-flex p-ai-center" style={{ gap: '14px', fontSize: '13px' }}>
                                <span style={{ cursor: 'pointer', color: '#0A1F44', fontWeight: 600 }} onClick={() => setAllChecked(true)}>Select all</span>
                                <span style={{ cursor: 'pointer', color: '#64748B' }} onClick={() => setAllChecked(false)}>Clear all</span>
                            </span>
                        )}
                    </div>

                    <div style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                        <div className="p-d-flex p-ai-center p-jc-between"
                            style={{ padding: '10px 14px', background: '#0A1F44', color: '#fff',
                                position: 'sticky', top: 0, zIndex: 2 }}>
                            <span style={{ flex: 1, fontWeight: 600, fontSize: '13px', letterSpacing: '.3px' }}>MENU / PAGE</span>
                            <div className="p-d-flex" style={{ width: FLAGS_WIDTH, flex: '0 0 auto' }}>
                                {FLAGS.map(([, label]) => (
                                    <div key={label} style={{ width: FLAG_COL, textAlign: 'center', fontSize: '12px', fontWeight: 600 }}>{label}</div>
                                ))}
                            </div>
                        </div>

                        {serviceList.length === 0
                            ? <div style={{ padding: '16px', color: '#64748B' }}>No manageable pages.</div>
                            : parents.map(parent => (
                                <div key={parent.ServiceId}>
                                    {menuRow(parent, false)}
                                    {serviceList
                                        .filter(c => c.IsParent === 0 && c.ParentServiceID === parent.ServiceName)
                                        .map(child => menuRow(child, true))}
                                </div>
                            ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserAccessManagement;
